import React, { FC, useEffect, useState } from 'react';
import { Box, Checkbox, Grid, Typography, Link, Radio } from '@material-ui/core';
import Image from 'material-ui-image';
import clsx from 'clsx';
import featureAccessoryStyles from './featureAccessoryStyles';
import IFeature from '../../../../../model/IFeature';
import OptionUnavailable from '../../../../../../common/assets/images/optionUnavailable.png';
import ConfigurablePrice from '../../../../presentational/prices/configurablePrice/ConfigurablePrice';
import featuresPerRowService from '../../../../../service/featuresPerRowService';
import ICategoryAttributes from '../../../../../model/ICategoryAttributes';
import Translation from '../../../../../../gqlCommon/components/translation/Translation';
import { FeatureType } from '../../../../../enum/FeatureType';
import useTheme from '@material-ui/core/styles/useTheme';
import { ISpcTheme } from '../../../../../../common/theme/ISpcTheme';
import SpecificationDisclaimer from '../../../../../../gqlCommon/components/presentation/specificationDisclaimer/SpecificationDisclaimer';
import useCustomizeCarContext from '../../../../../hooks/useCustomizeCarContext';
import HandleFeatureAccessoryDataLayer from '../../../../../../common/utils/dataLayer/graphql/handleFeatureAccessoryDataLayer';
import useWidgetContext from '../../../../../../common/react/useWidgetContext';

export interface IFeatureProps {
  feature: IFeature;
  id: string;
  onClick: (id: string, category?: string, featureName?: string) => void;
  isSelectedFeature: boolean;
  categoryAttributes?: ICategoryAttributes;
  orientation?: FeatureAccessoryOrientation;
  openAccessoryDetailsModal: (value: boolean, feature: IFeature) => void;
}

export enum FeatureAccessoryOrientation {
  PORTRAIT = 'portrait',
  LANDSCAPE = 'landscape',
}

const FeatureAccessory: FC<IFeatureProps> = ({
  feature,
  onClick,
  id,
  isSelectedFeature,
  categoryAttributes,
  orientation = FeatureAccessoryOrientation.PORTRAIT,
  openAccessoryDetailsModal,
}) => {
  const styles = featureAccessoryStyles();
  const [isChecked, setIsChecked] = useState<boolean>(isSelectedFeature);
  const {
    name,
    prices,
    images,
    shortDescription,
    attributes: { extradetails, disclaimer },
    code,
  } = feature;
  const gridSize = featuresPerRowService.getFeaturesPerRow(categoryAttributes?.numberofitemsperrow || '1');
  const isPortraitMode = orientation === FeatureAccessoryOrientation.PORTRAIT;
  const { optimisticSelectedFeatures: selectedFeatures } = useCustomizeCarContext();
  const {
    spc: {
      customise: { showAccessoryPriceOnNewRow, accessoryImageAspectRatio },
    },
  } = useTheme<ISpcTheme>();
  const isDisabled = feature.attributes.isDefaultItem || false;
  const {
    configuration: { dimensions },
  } = useWidgetContext();

  useEffect(() => {
    setIsChecked(isSelectedFeature);
  }, [isSelectedFeature, selectedFeatures]);

  const onSelection = () => {
    setIsChecked(!isChecked);
    if (!isChecked) {
      HandleFeatureAccessoryDataLayer(name, dimensions);
    }
  };

  return (
    <Grid
      item={true}
      xs={gridSize || 12}
      className={clsx([
        styles.container,
        {
          [styles.selectedFeatureContainer]: isSelectedFeature,
        },
      ])}
      onClick={onSelection}
    >
      <Box
        onClick={() => onClick(id, categoryAttributes?.categorypurpose, feature?.name)}
        className={clsx([
          styles.itemContainer,
          {
            [styles.itemContainerDisabled]: isDisabled,
          },
        ])}
        flexDirection={isPortraitMode ? 'column' : 'row'}
        data-test={`option:${code}`}
      >
        {isDisabled && <Box className={styles.backdrop} data-test={`option:disabled:${feature.code}`} />}
        <Box width={isPortraitMode ? '100%' : '50%'}>
          <Image
            key={images[0]?.url}
            src={images[0]?.url || 'error'}
            alt={name}
            animationDuration={0}
            color={'transparent'}
            aspectRatio={accessoryImageAspectRatio || 1.8}
            data-test={'accessory:image'}
            errorIcon={
              <img
                className={styles.imageUnavailable}
                src={OptionUnavailable}
                alt={name}
                data-test={'accessory:image:unavailable'}
              />
            }
          />
        </Box>
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="space-between"
          pt="10px"
          px="10px"
          pb={isPortraitMode ? '20px' : '10px'}
          width={isPortraitMode ? 'unset' : '50%'}
          className={styles.detailsSectionWrapper}
        >
          <Box display="flex" flexDirection="column" className={styles.detailsSection}>
            <Box
              display="flex"
              justifyContent="space-between"
              flexDirection={isPortraitMode && !showAccessoryPriceOnNewRow ? 'row' : 'column'}
              width="100%"
              className={styles.detailsWrapper}
            >
              <Typography className={styles.detailsName} component="p" variant="caption">
                {name}
                {disclaimer && <SpecificationDisclaimer disclaimer={disclaimer} />}
              </Typography>
              {categoryAttributes?.displayfeatureprice ? (
                <ConfigurablePrice
                  classes={{ multi: { container: styles.featureAccessoryPrice } }}
                  prices={prices}
                  display={{ showOrLabel: false }}
                />
              ) : null}
            </Box>
            {shortDescription && (
              <Typography component="p" variant="caption">
                {shortDescription}
              </Typography>
            )}
          </Box>
          <Box
            display="flex"
            alignItems="center"
            className={isPortraitMode ? styles.inputSectionPortrait : styles.inputSectionLandscape}
          >
            {extradetails && (
              <Link
                className={styles.moreDetailsToggle}
                variant={'helpingText'}
                underline={'always'}
                color={'secondary'}
                data-test={'button:accessory:modal'}
                onClick={(ev: React.MouseEvent<HTMLAnchorElement>) => {
                  ev.stopPropagation();
                  openAccessoryDetailsModal(true, feature);
                }}
              >
                <Translation id="trans__configurator__customise_page_feature_extra_details__details_link" />
              </Link>
            )}
            <Box ml="auto">
              {!categoryAttributes.selectiontype || categoryAttributes.selectiontype === FeatureType.multi ? (
                <Checkbox
                  checked={isChecked}
                  size={'medium'}
                  color={'primary'}
                  disabled={isDisabled}
                  data-test={`option:type:checkbox`}
                  data-selected={isChecked}
                  className={styles.checkbox}
                />
              ) : (
                <Radio
                  checked={isChecked}
                  value={id}
                  disabled={isDisabled}
                  edge={'end'}
                  color={'primary'}
                  data-test={`option:type:radio`}
                  className={styles.radio}
                />
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Grid>
  );
};

export default FeatureAccessory;
